import React, { useState, useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const labelStyle = {
  fontFamily: "'EB Garamond', Georgia, serif",
  fontSize: '17px',
  color: 'rgba(255, 243, 225, 0.65)',
  lineHeight: 1.6,
  display: 'block',
  marginBottom: '10px',
};

const textareaStyle = {
  width: '100%',
  background: 'rgba(255, 243, 225, 0.04)',
  border: '0.5px solid rgba(255, 243, 225, 0.10)',
  borderRadius: '8px',
  padding: '14px 16px',
  color: 'rgba(255, 243, 225, 0.82)',
  fontFamily: "'EB Garamond', Georgia, serif",
  fontSize: '16px',
  lineHeight: 1.7,
  resize: 'vertical',
  minHeight: '80px',
  outline: 'none',
  boxSizing: 'border-box',
};

const DeleteAccountView = ({ onDeleted, onBack }) => {
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState('');
  const [improvements, setImprovements] = useState('');
  const [wouldReturn, setWouldReturn] = useState('');
  const [openFeedback, setOpenFeedback] = useState('');
  const [rating, setRating] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [farewellVisible, setFarewellVisible] = useState(false);

  // After farewell shown, auto-navigate
  useEffect(() => {
    if (step === 3) {
      const t1 = setTimeout(() => setFarewellVisible(true), 100);
      const t2 = setTimeout(() => onDeleted(), 6000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [step, onDeleted]);

  const handleDelete = async () => {
    setIsDeleting(true);
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/auth/account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reason,
          improvements,
          would_return: wouldReturn,
          open_feedback: openFeedback,
          rating,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Something went wrong. Please try again.');
        setIsDeleting(false);
        return;
      }
      // Clear local state
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_email');
      localStorage.removeItem('user_id');
      localStorage.removeItem('user_name');
      setStep(3);
    } catch {
      setError('Could not connect to server.');
      setIsDeleting(false);
    }
  };

  // Step 3: Farewell
  if (step === 3) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: '#080608',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px',
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '360px',
          opacity: farewellVisible ? 1 : 0,
          transition: 'opacity 2s ease',
        }}>
          <div className="breathing-sphere" style={{ margin: '0 auto 56px' }} />
          <p style={{
            fontFamily: "'Cormorant', Georgia, serif",
            fontStyle: 'italic',
            fontSize: '26px',
            fontWeight: 400,
            color: 'rgba(255, 243, 225, 0.75)',
            lineHeight: 1.5,
            margin: '0 0 20px',
          }}>
            Take care of yourself.
          </p>
          <p style={{
            fontFamily: "'EB Garamond', Georgia, serif",
            fontSize: '16px',
            color: 'rgba(255, 243, 225, 0.35)',
            lineHeight: 1.8,
            margin: 0,
          }}>
            Your profile has been removed.<br />
            You're always welcome back.
          </p>
        </div>
      </div>
    );
  }

  // Step 2: Confirmation
  if (step === 2) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#080608',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px',
      }}>
        <div style={{ width: '100%', maxWidth: '400px', textAlign: 'center' }} className="fade-in">
          <p style={{
            fontFamily: "'Cormorant', Georgia, serif",
            fontStyle: 'italic',
            fontSize: '24px',
            fontWeight: 400,
            color: 'rgba(255, 243, 225, 0.75)',
            lineHeight: 1.5,
            marginBottom: '16px',
          }}>
            This cannot be undone.
          </p>
          <p style={{
            fontFamily: "'EB Garamond', Georgia, serif",
            fontSize: '16px',
            color: 'rgba(255, 243, 225, 0.40)',
            lineHeight: 1.8,
            marginBottom: '48px',
          }}>
            Your conversations, history, and account<br />
            will be permanently removed.
          </p>

          {error && (
            <p style={{
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '14px',
              color: 'rgba(196, 137, 74, 0.8)',
              marginBottom: '24px',
            }}>
              {error}
            </p>
          )}

          <button
            onClick={handleDelete}
            disabled={isDeleting}
            data-testid="confirm-delete-account"
            style={{
              display: 'block',
              width: '100%',
              padding: '14px',
              background: 'transparent',
              border: '0.5px solid rgba(180, 60, 40, 0.3)',
              borderRadius: '8px',
              cursor: isDeleting ? 'default' : 'pointer',
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '17px',
              color: 'rgba(255, 120, 100, 0.65)',
              marginBottom: '16px',
              opacity: isDeleting ? 0.5 : 1,
              transition: 'opacity 0.2s ease',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {isDeleting ? 'Deleting…' : 'Delete my profile'}
          </button>

          <button
            onClick={() => { setStep(1); setError(''); }}
            data-testid="cancel-delete-account"
            style={{
              display: 'block',
              width: '100%',
              padding: '14px',
              background: 'transparent',
              border: '0.5px solid rgba(255, 243, 225, 0.10)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '17px',
              color: 'rgba(255, 243, 225, 0.45)',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            Keep my profile
          </button>
        </div>
      </div>
    );
  }

  // Step 1: Survey
  return (
    <div style={{
      minHeight: '100vh',
      background: '#080608',
      padding: '0 0 80px',
    }}>
      <div style={{ maxWidth: '520px', margin: '0 auto', padding: '64px 32px 0' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'rgba(255, 243, 225, 0.30)',
            fontFamily: "'EB Garamond', Georgia, serif",
            fontSize: '15px',
            padding: 0,
            marginBottom: '56px',
            display: 'block',
          }}
        >
          ← back
        </button>

        <h1 style={{
          fontFamily: "'Cormorant', Georgia, serif",
          fontStyle: 'italic',
          fontWeight: 400,
          fontSize: '32px',
          color: 'rgba(255, 243, 225, 0.78)',
          marginBottom: '48px',
          letterSpacing: '-0.01em',
        }}>
          Before you go.
        </h1>

        <p style={{
          fontFamily: "'EB Garamond', Georgia, serif",
          fontSize: '16px',
          color: 'rgba(255, 243, 225, 0.40)',
          lineHeight: 1.7,
          marginBottom: '48px',
        }}>
          You don't have to answer any of these. But if you do, it genuinely helps.
        </p>

        {/* Q1: Why */}
        <div style={{ marginBottom: '36px' }}>
          <label style={labelStyle}>What brought you to this decision?</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="No pressure — share whatever feels right."
            style={textareaStyle}
            data-testid="delete-reason"
          />
        </div>

        {/* Q2: Star rating */}
        <div style={{ marginBottom: '36px' }}>
          <label style={labelStyle}>How would you rate your experience overall?</label>
          <div style={{ display: 'flex', gap: '12px' }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                data-testid={`rating-star-${star}`}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '24px',
                  color: star <= rating ? 'rgba(196, 137, 74, 0.85)' : 'rgba(255, 243, 225, 0.18)',
                  padding: '4px',
                  lineHeight: 1,
                  transition: 'color 0.15s ease',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                ✦
              </button>
            ))}
          </div>
        </div>

        {/* Q3: Improvements */}
        <div style={{ marginBottom: '36px' }}>
          <label style={labelStyle}>Is there anything we could have done differently?</label>
          <textarea
            value={improvements}
            onChange={(e) => setImprovements(e.target.value)}
            placeholder="Honest feedback is welcome."
            style={textareaStyle}
            data-testid="delete-improvements"
          />
        </div>

        {/* Q4: Open feedback */}
        <div style={{ marginBottom: '36px' }}>
          <label style={labelStyle}>Anything else you'd like us to know?</label>
          <textarea
            value={openFeedback}
            onChange={(e) => setOpenFeedback(e.target.value)}
            placeholder=""
            style={textareaStyle}
            data-testid="delete-open-feedback"
          />
        </div>

        {/* Q5: Would return */}
        <div style={{ marginBottom: '56px' }}>
          <label style={labelStyle}>Would you consider coming back someday?</label>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {['Yes', 'Maybe', 'No'].map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setWouldReturn(opt)}
                data-testid={`would-return-${opt.toLowerCase()}`}
                style={{
                  padding: '10px 24px',
                  background: wouldReturn === opt ? 'rgba(196, 137, 74, 0.12)' : 'transparent',
                  border: `0.5px solid ${wouldReturn === opt ? 'rgba(196, 137, 74, 0.35)' : 'rgba(255, 243, 225, 0.10)'}`,
                  borderRadius: '100px',
                  cursor: 'pointer',
                  fontFamily: "'EB Garamond', Georgia, serif",
                  fontSize: '16px',
                  color: wouldReturn === opt ? 'rgba(196, 137, 74, 0.9)' : 'rgba(255, 243, 225, 0.45)',
                  transition: 'all 0.15s ease',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
          <button
            onClick={() => setStep(2)}
            data-testid="proceed-to-delete-confirm"
            style={{
              display: 'block',
              width: '100%',
              padding: '14px',
              background: 'transparent',
              border: '0.5px solid rgba(180, 60, 40, 0.25)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '17px',
              color: 'rgba(255, 120, 100, 0.55)',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            Delete my profile
          </button>

          <button
            onClick={onBack}
            data-testid="skip-delete-survey"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '14px',
              color: 'rgba(255, 243, 225, 0.22)',
              padding: '8px',
              letterSpacing: '0.03em',
            }}
          >
            Skip — keep my profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountView;
